import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import useRegistrationQuery from 'blocks/Authentication/hooks/useRegistrationQuery';
import { removeReferralQueryParams } from 'blocks/Authentication/utils';
import { redeemPromotion, tierSelector } from 'redux/modules/pricing';
import { Dispatch } from 'redux/types';

type AlertStatus = 'idle' | 'in-progress' | 'resolved';

export default function useRedeemPromotion() {
  const dispatch = useDispatch<Dispatch>();
  const location = useLocation();
  const history = useHistory();
  const tier = useSelector(tierSelector);
  const { promoKey } = useRegistrationQuery();
  const alertStatus = useRef<AlertStatus>('idle');

  useEffect(() => {
    // if tier is null/undefined, we cannot yet determine if the user is on a plan
    // that is eligible to redeem the promotion.  a persisted, rehydrated tier comes
    // back as null (not undefined), so both must be guarded against.  if redeeming is
    // in progress, we should bail in order to prevent showing the wrong modal/state
    if (
      !promoKey ||
      tier === undefined ||
      tier === null ||
      alertStatus.current !== 'idle'
    ) {
      return;
    }

    const handlePromotion = async () => {
      alertStatus.current = 'in-progress';

      try {
        await dispatch(redeemPromotion(promoKey));
      } finally {
        // remove the query params - if the user refreshes the page and the query
        // params are still there, they will see a modal that says they are not
        // eligible for the promotion
        history.replace(removeReferralQueryParams(location));
        alertStatus.current = 'resolved';
      }
    };

    handlePromotion();
  }, [dispatch, history, location, promoKey, tier]);
}
